import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import CommandPalette from "../lib/command_palette/command_palette"
import KeySequenceHandler from "../lib/command_palette/key_sequence_handler"

const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable], trix-editor'
const HIDDEN_CLASS = 'command-palette--hidden'
const MAC_PLATFORM = /Mac|iPhone|iPad/

export default class extends Controller {
  static targets = ['palette', 'input', 'results', 'shortcuts', 'modifier']
  static values = {
    commands: Array,
    messages: Object
  }

  connect() {
    this.palette = new CommandPalette({
      dialog: this.paletteTarget,
      input: this.inputTarget,
      results: this.resultsTarget,
      commands: this.commandsValue,
      messages: this.messagesValue
    })
    this.keySequenceHandler = new KeySequenceHandler()
    this.keyBindings = Object.fromEntries(
      this.commandsValue.map(command => [command.keyBinding, () => this.executeCommand(command)])
    )

    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.handleKeydown)
    this.labelModifierKeys()
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleKeydown)
    this.keySequenceHandler.cleanup()
    this.palette.close()
  }

  handleKeydown(event) {
    if (this.shortcutsOpen()) {
      if (event.key === 'Escape' || this.isModifierShortcut(event, '/')) {
        event.preventDefault()
        this.closeShortcuts()
      }
      return
    }

    if (this.isModifierShortcut(event, 'k')) {
      event.preventDefault()
      this.palette.isOpen() ? this.palette.close() : this.palette.openCommandList()
      return
    }

    if (this.isModifierShortcut(event, '/')) {
      event.preventDefault()
      this.palette.close()
      this.openShortcuts()
      return
    }

    if (this.palette.isOpen()) {
      this.handlePaletteKeydown(event)
      return
    }

    if (event.target.closest(EDITABLE_SELECTOR)) return

    this.keySequenceHandler.handle(event, this.keyBindings)
  }

  handlePaletteKeydown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault()
        this.palette.close()
        break
      case 'ArrowDown':
        event.preventDefault()
        this.palette.navigateDown()
        break
      case 'ArrowUp':
        event.preventDefault()
        this.palette.navigateUp()
        break
      case 'Enter':
        event.preventDefault()
        this.activateSelection(this.wantsNewTab(event))
        break
    }
  }

  activateSelection(newTab = false) {
    const commandKey = this.palette.activeCommandKey()
    if (commandKey) {
      this.executeCommandByKey(commandKey, newTab)
      return
    }

    const url = this.palette.activeElement()?.getAttribute('href')
    if (url) this.visit(url, newTab)
  }

  executeActiveCommand(event) {
    this.executeCommandByKey(event.currentTarget.dataset.commandKey, this.wantsNewTab(event))
  }

  executeCommandByKey(key, newTab = false) {
    const command = this.commandsValue.find(candidate => candidate.key === key)
    if (command) this.executeCommand(command, newTab)
  }

  executeCommand(command, newTab = false) {
    if (command.type === 'search') {
      this.palette.openSearch(command.path, this.messagesValue.searchPlaceholder.replace('%{command}', command.name))
    } else {
      this.visit(command.path, newTab)
    }
  }

  visit(url, newTab = false) {
    this.palette.close()
    newTab ? window.open(url, '_blank', 'noopener') : Turbo.visit(url)
  }

  wantsNewTab(event) {
    return event.metaKey || event.ctrlKey
  }

  search(event) {
    this.palette.handleInput(event.target.value.trim())
  }

  visitResult(event) {
    event.preventDefault()
    this.visit(event.currentTarget.getAttribute('href'), this.wantsNewTab(event))
  }

  closeOnBackdrop(event) {
    if (event.target === this.paletteTarget) this.palette.close()
    if (event.target === this.shortcutsTarget) this.closeShortcuts()
  }

  openShortcuts() {
    this.shortcutsTarget.classList.remove(HIDDEN_CLASS)
  }

  closeShortcuts() {
    this.shortcutsTarget.classList.add(HIDDEN_CLASS)
  }

  shortcutsOpen() {
    return !this.shortcutsTarget.classList.contains(HIDDEN_CLASS)
  }

  isModifierShortcut(event, key) {
    return (event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === key
  }

  isMac() {
    return MAC_PLATFORM.test(navigator.userAgentData?.platform || navigator.platform)
  }

  labelModifierKeys() {
    const label = this.isMac() ? '⌘' : 'Ctrl'
    this.modifierTargets.forEach(target => { target.textContent = label })
  }
}
